import {
  Button, Checkbox, DialogActions, DialogContent, FormControlLabel, TextField
} from '@mui/material';
import {
  CustomTooltip, FlexContainer, FontIcon, Question, SelectCountryDetail, Text
} from 'components';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useStores } from 'hooks';
import { CountryView, InterventionView } from 'models';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InterventionsService } from 'services';
import { APPLICATION_ROLES } from 'utils/constants';
import { InterventionHelper, translate, UserHelper } from 'utils/helpers';
import { ModalHeader } from './ModalHeader';

export const CreateInterventionModal = ({
                                          onClose
                                        }) => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [generatedFileNumber, setGeneratedFileNumber] = useState(format(new Date(), 'ddMMyy-hhmm', { locale: fr }));
  const [fileNumberExist, setFileNumberExist] = useState(null);
  const [INTERVENTION_STATUS, setInterventionStatus] = useState(null);
  const [country, setCountry] = useState<CountryView>(null);

  const { control, watch, setValue } = useForm({
    defaultValues: {
      fileNumber: generatedFileNumber,
      isGeneratedFileNumber: true
    }
  });

  const isGeneratedFileNumber = watch('isGeneratedFileNumber');
  const fileNumber = watch('fileNumber');

  const currentFileNumber = encodeURIComponent(isGeneratedFileNumber ? generatedFileNumber : fileNumber);
  const newAppointment = InterventionHelper.getFormattedDateTime(new Date());
  const defaultIntervention = new InterventionView();

  useEffect(() => {
    InterventionHelper.loadInterventionStatuses().then(IS => setInterventionStatus(IS));

    InterventionsService.getCustomFileNumber().then((response) => {
      if (response) {
        setGeneratedFileNumber(response);
        setValue('fileNumber', response);
      }
    });
    // eslint-disable-next-line
  }, []);

  class PathView {
    pathname: string;
    state: any;
  }

  const handleValidateModal = (link: string | PathView) => {
    // Check if the fileNumber doesn't exist already
    InterventionsService.checkFileNumber(currentFileNumber).then((response) => {
      if (response) {
        return setFileNumberExist(translate('errors.fileNumberAlreadyExist'));
      }

      if (response === false) {
        // Process the link
        setFileNumberExist(null);
        if (typeof link === 'string') {
          navigate(link);
        } else {
          navigate(link.pathname, { state: link.state });
        }
        return onClose();
      }

      return null;
    }).catch(() => {
      if (typeof link === 'string') {
        navigate(link);
      } else {
        navigate(link.pathname, { state: link.state });
      }
      onClose();
    });
  };

  const handlePlanIntervention = () => {
    handleValidateModal({
      pathname: `/interventions/${currentFileNumber}/plan`,
      state: {
        isNew: true,
        country: JSON.parse(JSON.stringify(country))
      }
    });
  };

  const handleStartIntervention = () => {
    const newIntervention: InterventionView = {
      ...defaultIntervention,
      country,
      fileNumber: currentFileNumber,
      appointment: newAppointment,
      status: INTERVENTION_STATUS?.ONGOING.key || 'ONGOING'
    };

    if (!userStore.isOffline) {
      return InterventionsService.checkFileNumber(currentFileNumber).then((response) => {
        if (response) {
          return setFileNumberExist(translate('errors.fileNumberAlreadyExist'));
        }

        if (response === false) {
          // Process the link
          setFileNumberExist(null);
          InterventionsService.startIntervention(currentFileNumber, newIntervention).then((resp) => {
            InterventionHelper.saveInterventionDetailAndForm(resp.hashId, resp).then(() => {
              InterventionHelper.loadInterventionFormStored(resp.hashId).then((storedInterventionForm) => {
                navigate(`/interventions/${resp.hashId}/inspection`, {
                  state: {
                    needToBeStored: true,
                    currentStep: storedInterventionForm.currentStep,
                    storedInterventionForm
                  }
                });
              });
            });
          }).catch(error => enqueueSnackbar(error?.message || error, { variant: 'error' }));
          return onClose();
        }

        return null;
      });
    }

    return InterventionHelper.saveInterventionDetailAndForm(currentFileNumber, newIntervention).then(() => {
      InterventionHelper.loadInterventionFormStored(currentFileNumber).then((storedInterventionForm) => {
        handleValidateModal({
          pathname: `/interventions/${currentFileNumber}/inspection`,
          state: {
            needToBeStored: true,
            currentStep: storedInterventionForm.currentStep,
            storedInterventionForm
          }
        });
      });
    });
  };

  return (
    <form>
      <ModalHeader onClose={onClose}>
        <FontIcon fontSize="2.6rem" icon="icon-create-intervention" marginRight="1rem" />
        {translate('modalIntervention.newIntervention')}
      </ModalHeader>

      <DialogContent style={{ paddingTop: '10px' }}>
        <Question label={`${translate('modalIntervention.cerfaNumber')} :`}>
          {fileNumberExist && <Text color="var(--red)" fontWeight={600} margin="0">{fileNumberExist}</Text>}
          <FlexContainer alignItems="center">
            <Controller
                  control={control}
                  name="isGeneratedFileNumber"
                  render={({ field }) => (
                    <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} />}
                          data-cy="createInterventionFileNumber"
                          label={`${translate('modalIntervention.useGeneratedNumber')} (${generatedFileNumber})`}
                      />
                  )}
              />
            <CustomTooltip text={translate('modalIntervention.tooltipText')} />
          </FlexContainer>
          {!isGeneratedFileNumber && (
            <Controller
                    control={control}
                    name="fileNumber"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            inputProps={{ maxLength: 32 }}
                            label={translate('common.cerfaNumber')}
                        />
                    )}
                />
            )}
          <Question label={`${translate('modalIntervention.country')} :`}>
            <SelectCountryDetail
                  required
                  value={country}
                  onChange={setCountry}
              />
          </Question>
        </Question>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{translate('button.cancel')}</Button>
        {!userStore.isOffline && (
          <Button
                  color="primary"
                  data-cy="planIntervention"
                  disabled={!currentFileNumber}
                  onClick={handlePlanIntervention}
              >
            {translate('button.planIntervention')}
          </Button>
          )}
        {UserHelper.hasAccessRight([APPLICATION_ROLES.OPERATOR]) && (
          <Button
                  color="primary"
                  disabled={!currentFileNumber}
                  onClick={handleStartIntervention}
              >
            {translate('button.startIntervention')}
          </Button>
          )}
      </DialogActions>
    </form>
  );
};
